// frontend/src/features/inventory/pages/StockPage.tsx
// ADR-074: موجودی لحظه‌ای + لیست تراکنش‌های انبار

import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { usePermission } from "@/auth/usePermission";
import { useQuery } from "@tanstack/react-query";
import {
  useV1InventoryStockTransactionsList,
  useV1InventoryWarehousesList,
  useV1InventoryStockTransactionsCreate,
  useV1InventoryStockBalancesList,
  useV1InventoryLotsList,
  v1InventoryStockTransactionsList,
  v1InventoryMaterialsList,
} from "@/api/generated/v1/v1";
import { DataTableToolbar } from "@/shared/components/DataTableToolbar";
import { ScannableInput } from "../components/ScannableInput";
import { ScannableMaterialPicker } from "../components/ScannableMaterialPicker";
import { axiosInstance } from "@/api/client";
import { exportToExcel, exportToPDF } from "@/shared/utils/export";
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import gregorian from "react-date-object/calendars/gregorian";
import { PageBanner } from "@/shared/components/PageBanner";
import { useLotLookup } from "../lookup/useLotLookup";
import { applyLotToForm, extractApiErrorMessage, remainingInWarehouse, warehouseMismatchMessage } from "../lookup/lotScan";
import type { LotLookupResponse } from "../lookup/lotLookupTypes";
import { StockTransferForm } from "../lookup/StockTransferForm";


const TX_TYPE_LABEL: Record<string, string> = {
  receipt: "ورود",
  issue: "خروج",
  transfer_in: "انتقال ورودی",
  transfer_out: "انتقال خروجی",
  opening_balance: "موجودی اول دوره",
};

export function StockPage() {
  const canManage = usePermission("inventory.manage");
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<"on-hand" | "transactions">("on-hand");
  const [showForm, setShowForm] = useState(false);
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [materialId, setMaterialId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [txType, setTxType] = useState("receipt");
  const [quantity, setQuantity] = useState("");
  const [referenceDoc, setReferenceDoc] = useState("");
  const [transactionDate, setTransactionDate] = useState<DateObject | null>(new DateObject());
  const [error, setError] = useState<string | null>(null);

  // AM-036 Phase 2 lookup hook and state
  const { lookup: lookupLot, isScanning } = useLotLookup();
  const [scannedLot, setScannedLot] = useState<LotLookupResponse | null>(null);
  const [maxQuantity, setMaxQuantity] = useState<string | null>(null);

  // ADR-075: state‌های مربوط به لات
  const [createLot, setCreateLot] = useState(false);
  const [lotsList, setLotsList] = useState([{ id: 1, quantity: "", numerator: "", denominator: "" }]);
  const [selectedLotId, setSelectedLotId] = useState("");
  const [createdLotId, setCreatedLotId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);

  const { data: txData, isLoading: txLoading, refetch: refetchTx } =
    useV1InventoryStockTransactionsList(
      { search: searchTerm || undefined, ...activeFilters } as any,
      { query: { enabled: activeTab === "transactions" } }
    );

  const { data: onHandData, isLoading: onHandLoading } =
    useV1InventoryStockBalancesList({ query: { enabled: activeTab === "on-hand" } });

  const { data: warehousesData } =
    useV1InventoryWarehousesList(undefined, { query: {} });

  // AM-036 Phase 1: Fetch all material pages for local scanning
  const { data: materials = [] } = useQuery({
    queryKey: ["all-materials"],
    queryFn: async () => {
      let all: any[] = [];
      let page = 1;
      const MAX_PAGES = 50;
      try {
        while (page <= MAX_PAGES) {
          const res = await v1InventoryMaterialsList({ page, page_size: 1000 });
          all = [...all, ...(res.results || [])];
          if (!res.next) break;
          page++;
        }
        if (page > MAX_PAGES) {
          throw new Error("بیش از ۵۰ صفحه متریال واکشی شد.");
        }
        return all;
      } catch (err) {
        console.error("Failed to fetch materials", err);
        throw err; // Ensure we don't return partial list and cache it as success
      }
    },
  });

  // ADR-075 Rule DU-04: لیست پالت‌های موجود برای متریال انتخاب‌شده
  const { data: lotsData } = useV1InventoryLotsList(
    { material_revision__family: materialId } as any,
    { query: { enabled: !!materialId && (txType === "issue" || txType === "transfer_out") } }
  );
  const lots = lotsData?.results ?? [];

  const handleLotScan = async (code: string) => {
    setError(null);
    if (txType === "receipt" || txType === "opening_balance" || txType === "transfer_in") {
      setError("اسکن پالت فقط برای خروج/انتقال معتبر است.");
      return;
    }

    const result = await lookupLot(code);
    if (result.status === "cancelled") return;
    if (result.status === "error") {
      setScannedLot(null);
      setSelectedLotId("");
      setError(result.message);
      return;
    }
    const outcome = applyLotToForm(result.lot, txType as any);
    if (!outcome.ok) {
      setScannedLot(null);
      setSelectedLotId("");
      setError(outcome.message);
      return;
    }
    setScannedLot(result.lot);
    setMaterialId(outcome.patch.materialId);
    setSelectedLotId(outcome.patch.lotId);
    setWarehouseId(outcome.patch.warehouseId);
    setMaxQuantity(outcome.patch.maxQuantity);
  };

  const createStockTx = useV1InventoryStockTransactionsCreate({});
  const mutateAsync = createStockTx.mutateAsync;
  const isPending = createStockTx.isPending;

  const handleSubmit = async () => {
    setError(null);
    if (!materialId) { setError("متریال الزامی است"); return; }
    if (!warehouseId) { setError("انبار الزامی است"); return; }
    if (!quantity || isNaN(Number(quantity)) || Number(quantity) <= 0) {
      setError("مقدار کل باید عدد مثبت باشد");
      return;
    }

    const txDateStr = transactionDate ? transactionDate.convert(gregorian).format("YYYY-MM-DD") : undefined;

    if (txType === "receipt" && createLot) {
      const totalLotQty = lotsList.reduce((acc, lot) => acc + Number(lot.quantity || 0), 0);
      if (Math.abs(totalLotQty - Number(quantity)) > 0.0001) {
        setError(`مجموع مقادیر پالت‌ها (${totalLotQty}) با مقدار کل رسید (${quantity}) برابر نیست.`);
        return;
      }

      let successCount = 0;
      let errors: string[] = [];
      let lastCreatedLotId = "";

      for (let i = 0; i < lotsList.length; i++) {
        const lot = lotsList[i]!;
        try {
          const res = await mutateAsync({
            data: {
              material: materialId,
              warehouse: warehouseId,
              transaction_type: txType as any,
              quantity: lot.quantity,
              reference_doc: referenceDoc.trim(),
              ...(txDateStr ? { transaction_date: txDateStr } : {}),
              create_lot: true,
              ...(lot.numerator ? { lot_qty_numerator: Number(lot.numerator) } : {}),
              ...(lot.denominator ? { lot_qty_denominator: Number(lot.denominator) } : {}),
            } as any
          });
          successCount++;
          if (res) {
            lastCreatedLotId = String((res as any).lot_id || (res as any).lot || (res as any).id);
          }
        } catch (err: any) {
          const detail = err?.response?.data?.detail || err?.response?.data || err.message;
          errors.push(`پالت ردیف ${i + 1}: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
        }
      }

      if (errors.length > 0) {
        setError(`${successCount} پالت با موفقیت ثبت شد، اما ${errors.length} پالت خطا داشت:\n${errors.join('\n')}`);
        refetchTx();
      } else {
        if (lotsList.length === 1 && lastCreatedLotId) {
          setCreatedLotId(lastCreatedLotId);
          // If only 1 lot, we don't close form so they can print
        } else {
          setShowForm(false);
          setCreatedLotId("");
        }
        setMaterialId("");
        setWarehouseId("");
        setQuantity("");
        setReferenceDoc("");
        setLotsList([{ id: 1, quantity: "", numerator: "", denominator: "" }]);
        setCreateLot(false);
        refetchTx();
      }
      return;
    }

    // Normal single transaction (No createLot or not a receipt)
    try {
      await mutateAsync({
        data: {
          material: materialId,
          warehouse: warehouseId,
          transaction_type: txType as any,
          quantity: quantity,
          reference_doc: referenceDoc.trim(),
          ...(txDateStr ? { transaction_date: txDateStr } : {}),
          ...((txType === "issue" || txType === "transfer_out") && selectedLotId
            ? { lot_id: selectedLotId }
            : {}),
        } as any,
      });
      setShowForm(false);
      setMaterialId("");
      setWarehouseId("");
      setQuantity("");
      setReferenceDoc("");
      setSelectedLotId("");
      setError(null);
      refetchTx();
    } catch (err: any) {
      const responseData = err?.response?.data;
      setError(extractApiErrorMessage(responseData, err?.message || "خطا در ثبت تراکنش"));
    }
  };

  const transactions = txData?.results ?? [];
  const onHandStock = onHandData ?? [];
  const warehouses = warehousesData?.results ?? [];

  const [expandedGroups, setExpandedGroups] = useState<{ [key: string]: boolean }>({});

  const groupedTransactions = useMemo(() => {
    const groups: { [key: string]: any[] } = {};
    const singles: any[] = [];

    transactions.forEach((tx: any) => {
      if (tx.reference_doc && tx.transaction_type === "receipt") {
        const key = `${tx.reference_doc}_${tx.material}_${tx.warehouse}`;
        if (!groups[key]) groups[key] = [];
        groups[key].push(tx);
      } else {
        singles.push(tx);
      }
    });

    const groupArray = Object.values(groups).map((g) => {
      if (g.length === 1) return { ...g[0], isGroup: false };
      return {
        isGroup: true,
        id: `group_${g[0].reference_doc}_${g[0].material}`,
        reference_doc: g[0].reference_doc,
        material: g[0].material,
        warehouse: g[0].warehouse,
        transaction_type: g[0].transaction_type,
        quantity: g.reduce((sum: number, item: any) => sum + parseFloat(item.quantity || "0"), 0).toString(),
        created_at: g[0].created_at,
        lots: g,
      };
    });

    const all = [...groupArray, ...singles.map((s) => ({ ...s, isGroup: false }))];
    return all.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [transactions]);

  const toggleGroup = (id: string) => {
    setExpandedGroups(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleFilterChange = (name: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [name]: value }));
  };

  const fetchExportData = async () => {
    try {
      setIsExporting(true);
      if (activeTab === "on-hand") {
        // onHandStock is already fully loaded (no pagination on this custom endpoint)
        return onHandStock.map(item => ({
          ...item,
          warehouse_name: warehouses.find(w => w.id === String(item.warehouse_id))?.name || String(item.warehouse_id),
          material_code: materials.find(m => m.id === String(item.material_id))?.code || String(item.material_id),
        }));
      } else {
        const res = await v1InventoryStockTransactionsList({
          export: "true",
          ...(searchTerm && { search: searchTerm }),
          ...activeFilters
        } as any);
        if ((res as any).truncated) {
          alert("تعداد رکوردها بیش از ۱۰,۰۰۰ مورد است و اکسپورت محدود شد.");
        }
        return ((res as any).results || res).map((tx: any) => ({
          ...tx,
          warehouse_name: warehouses.find(w => w.id === String(tx.warehouse))?.name || String(tx.warehouse),
          material_code: materials.find(m => m.id === String(tx.material))?.code || String(tx.material),
        }));
      }
    } catch (err) {
      alert("خطا در دریافت اطلاعات برای خروجی");
      throw err;
    } finally {
      setIsExporting(false);
    }
  };

  const getColumns = () => {
    if (activeTab === "on-hand") {
      return [
        { header: "انبار", accessor: (row: any) => row.warehouse_name },
        { header: "متریال", accessor: (row: any) => row.material_code },
        { header: "موجودی", accessor: (row: any) => row.on_hand },
      ];
    } else {
      return [
        { header: "نوع", accessor: (row: any) => TX_TYPE_LABEL[row.transaction_type] || row.transaction_type },
        { header: "متریال", accessor: (row: any) => row.material_code },
        { header: "انبار", accessor: (row: any) => row.warehouse_name },
        { header: "مقدار", accessor: (row: any) => row.quantity },
        { header: "سند مرجع", accessor: (row: any) => row.reference_doc || "—" },
      ];
    }
  };

  const handleExportExcel = async () => {
    const rawData = await fetchExportData();
    await exportToExcel(rawData, getColumns(), activeTab === "on-hand" ? "stock_on_hand" : "stock_transactions");
  };

  const handleExportPDF = async () => {
    const rawData = await fetchExportData();
    await exportToPDF(rawData, getColumns(), activeTab === "on-hand" ? "stock_on_hand" : "stock_transactions");
  };

  const selectedMaterial = materials.find((m) => m.id === materialId);
  const rev = selectedMaterial?.latest_published_revision as any;
  const stockUom = rev?.stock_uom_code || (selectedMaterial as any)?.uom_code;
  const prodUom = rev?.production_uom_code;

  let uomInfoText = "";
  if (stockUom) {
    if (prodUom && prodUom !== stockUom) {
      uomInfoText = `واحد پایه: ${stockUom} | واحد دوم: ${prodUom}`;
    } else {
      uomInfoText = `واحد: ${stockUom}`;
    }
  }

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="تراکنش‌های انبار (Stock)"
        subtitle="ثبت اسناد ورود، خروج، حواله و انتقال موجودی انبار"
        icon="📊"
        helpLink="/help/inventory/stock"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate("/inventory")}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            ← داشبورد انبار
          </button>
          {canManage && activeTab === "transactions" && !showForm && (
            <>
              <button
                onClick={() => navigate("/inventory/lots")}
                style={{
                  padding: "0.6rem 1rem",
                  borderRadius: "12px",
                  background: "rgba(255, 255, 255, 0.15)",
                  color: "#ffffff",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                }}
              >
                پالت‌ها 📦
              </button>
              <button
                onClick={() => navigate("/inventory/stock-receipts/import")}
                style={{
                  padding: "0.6rem 1rem",
                  borderRadius: "12px",
                  background: "rgba(255, 255, 255, 0.15)",
                  color: "#ffffff",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  cursor: "pointer",
                }}
              >
                📥 ایمپورت Excel
              </button>
              <button
                onClick={() => {
                  setShowTransferForm(true);
                  setShowForm(false);
                }}
                style={{
                  padding: "0.6rem 1.25rem",
                  borderRadius: "12px",
                  background: "#ffffff",
                  color: "#007270",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  border: "none",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  transition: "all 0.2s ease",
                }}
              >
                <span>🔄</span>
                <span>انتقال بین انبارها</span>
              </button>
              <button
                onClick={() => {
                  setShowForm(true);
                  setShowTransferForm(false);
                }}
                style={{
                  padding: "0.6rem 1.25rem",
                  borderRadius: "12px",
                  background: "#ffffff",
                  color: "#007270",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  border: "none",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  transition: "all 0.2s ease",
                }}
              >
                <span>➕</span>
                <span>ثبت تراکنش</span>
              </button>
            </>
          )}
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>

        {/* فرم انتقال بین انبارها */}
        {showTransferForm && (
          <div
            className="mb-6 p-4"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid var(--glass-border)",
              borderRadius: "10px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h2 className="font-bold">انتقال بین انبارها</h2>
              <button
                className="btn btn-secondary"
                onClick={() => setShowTransferForm(false)}
                style={{ padding: "0.3rem 0.6rem", fontSize: "0.85rem" }}
              >
                ✕ بستن
              </button>
            </div>
            <StockTransferForm
              warehouses={warehouses.map((w: any) => ({ id: w.id, name: w.name }))}
              renderScanner={(onScanned, disabled) => (
                <ScannableInput
                  onScanned={onScanned}
                  disabled={disabled}
                  placeholder="کد پالت را اسکن کنید..."
                />
              )}
              onDone={() => {
                setShowTransferForm(false);
                refetchTx();
              }}
            />
          </div>
        )}

        {/* تب‌ها */}
        <div style={{ display: "flex", gap: "1rem", marginBottom: "1.5rem" }}>
          <button
            className={`btn ${activeTab === "on-hand" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setActiveTab("on-hand")}
          >
            موجودی لحظه‌ای
          </button>
          <button
            className={`btn ${activeTab === "transactions" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setActiveTab("transactions")}
          >
            لیست تراکنش‌ها
          </button>
        </div>

        <DataTableToolbar
          onSearch={setSearchTerm}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onExportExcel={handleExportExcel}
          onExportPDF={handleExportPDF}
          isExporting={isExporting}
          filters={[]}
          dateRangeFilters={
            activeTab === "transactions"
              ? [{ field: "created_at", label: "تاریخ تراکنش" }]
              : []
          }
        />

        {/* فرم ثبت تراکنش */}
        {showForm && (
          <div
            className="mb-6 p-4"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid var(--glass-border)",
              borderRadius: "10px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h2 className="font-bold">تراکنش جدید</h2>
              {uomInfoText && (
                <span style={{ fontSize: "0.85rem", color: "var(--color-primary)", background: "rgba(0,0,0,0.2)", padding: "0.3rem 0.6rem", borderRadius: "4px" }}>
                  {uomInfoText}
                </span>
              )}
            </div>

            {error && (
              <div
                className="mb-3 p-3"
                style={{
                  background: "rgba(255,0,0,0.1)",
                  borderLeft: "4px solid red",
                  borderRadius: "8px",
                  color: "red",
                  fontSize: "0.9rem",
                }}
              >
                {error}
              </div>
            )}

            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1rem" }}>

              {/* متریال */}
              <div style={{ flex: 2, minWidth: "180px" }}>
                <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  متریال *
                </label>
                <ScannableMaterialPicker
                  materials={materials.map((m: any) => ({
                    id: m.id,
                    code: m.code,
                    name: m.latest_published_revision?.name ?? "بدون ریویژن"
                  }))}
                  value={materialId}
                  onChange={(val) => setMaterialId(val)}
                  onScannedNonMaterial={handleLotScan}
                />
              </div>

              {/* انبار */}
              <div style={{ flex: 1, minWidth: "140px" }}>
                <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  انبار *
                </label>
                <select
                  value={warehouseId}
                  onChange={(e) => {
                    const wId = e.target.value;
                    setWarehouseId(wId);
                    if (scannedLot) {
                      const mismatch = warehouseMismatchMessage(scannedLot, wId);
                      if (mismatch) {
                        setError(mismatch);
                      } else {
                        setError(null);
                      }
                      setMaxQuantity(remainingInWarehouse(scannedLot, wId));
                    }
                  }}
                  style={{
                    width: "100%", padding: "0.6rem 0.9rem",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid var(--glass-border)",
                    borderRadius: "8px", color: "var(--color-text)",
                    boxSizing: "border-box",
                  }}
                >
                  <option value="">انتخاب کنید...</option>
                  {scannedLot && scannedLot.stock.length > 0
                    ? scannedLot.stock.map((s) => (
                        <option key={s.warehouse_id} value={s.warehouse_id}>
                          {s.warehouse_name ?? s.warehouse_id} — موجودی {s.qty}
                        </option>
                      ))
                    : warehouses.map((w) => (
                        <option key={w.id} value={w.id}>{w.name}</option>
                      ))}
                </select>
              </div>

              {/* نوع تراکنش */}
              <div style={{ flex: 1, minWidth: "140px" }}>
                <label htmlFor="txTypeSelect" style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  نوع تراکنش *
                </label>
                <select
                  id="txTypeSelect"
                  value={txType}
                  onChange={(e) => setTxType(e.target.value)}
                  style={{
                    width: "100%", padding: "0.6rem 0.9rem",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid var(--glass-border)",
                    borderRadius: "8px", color: "var(--color-text)",
                    boxSizing: "border-box",
                  }}
                >
                  {Object.entries(TX_TYPE_LABEL)
                    .filter(([k]) => k !== "transfer_in" && k !== "transfer_out")
                    .map(([val, lbl]) => (
                      <option key={val} value={val}>{lbl}</option>
                    ))}
                </select>
              </div>

              {/* تاریخ تراکنش */}
              <div style={{ flex: 1, minWidth: "140px" }}>
                <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  تاریخ تراکنش *
                </label>
                <DatePicker
                  value={transactionDate}
                  onChange={(date: DateObject | null) => setTransactionDate(date)}
                  calendar={persian}
                  locale={persian_fa}
                  calendarPosition="bottom-right"
                  containerStyle={{ width: "100%" }}
                  style={{
                    width: "100%", padding: "0.6rem 0.9rem",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid var(--glass-border)",
                    borderRadius: "8px", color: "var(--color-text)",
                    boxSizing: "border-box",
                    height: "38px"
                  }}
                />
              </div>

              {/* مقدار */}
              <div style={{ flex: 1, minWidth: "100px" }}>
                <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  مقدار *{maxQuantity ? ` (سقف: ${maxQuantity})` : ""}
                </label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="0"
                  min="0"
                  dir="ltr"
                  style={{
                    width: "100%", padding: "0.6rem 0.9rem",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid var(--glass-border)",
                    borderRadius: "8px", color: "var(--color-text)",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* سند مرجع */}
              <div style={{ flex: 2, minWidth: "160px" }}>
                <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  سند مرجع
                </label>
                <input
                  type="text"
                  value={referenceDoc}
                  onChange={(e) => setReferenceDoc(e.target.value)}
                  placeholder="شماره سفارش، فاکتور..."
                  dir="ltr"
                  style={{
                    width: "100%", padding: "0.6rem 0.9rem",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid var(--glass-border)",
                    borderRadius: "8px", color: "var(--color-text)",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              {/* فیلدهای مربوط به لات — ورود */}
              {txType === "receipt" && (
                <div style={{ flex: 3, minWidth: "300px", display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.9rem", color: "var(--color-text)" }}>
                    <input
                      type="checkbox"
                      checked={createLot}
                      onChange={(e) => setCreateLot(e.target.checked)}
                      style={{ width: "1.2rem", height: "1.2rem" }}
                    />
                    ثبت پالت‌ها برای این ورود
                  </label>
                  {createLot && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", border: "1px solid var(--glass-border)", padding: "1rem", borderRadius: "8px", background: "rgba(0,0,0,0.1)" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>لیست پالت‌ها</span>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: "0.2rem 0.5rem", fontSize: "0.8rem" }}
                          onClick={() => setLotsList([...lotsList, { id: lotsList.length + 1, quantity: "", numerator: "", denominator: "" }])}
                        >
                          + افزودن پالت
                        </button>
                      </div>
                      
                      {lotsList.map((lot, idx) => (
                        <div key={idx} style={{ display: "flex", gap: "0.5rem", alignItems: "center", background: "rgba(255,255,255,0.02)", padding: "0.5rem", borderRadius: "6px" }}>
                          <div style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", width: "15px" }}>{idx + 1}</div>
                          
                          <input
                            type="number"
                            placeholder="مقدار"
                            value={lot.quantity}
                             onChange={(e) => {
                               const newList = [...lotsList];
                               newList[idx]!.quantity = e.target.value;
                               setLotsList(newList);
                            }}
                            dir="ltr"
                            style={{ flex: 1, minWidth: "10px", padding: "0.5rem", background: "rgba(255,255,255,0.05)", border: "1px solid var(--glass-border)", borderRadius: "6px", color: "var(--color-text)", fontSize: "0.85rem" }}
                          />
                          
                          <input
                            type="number"
                            placeholder={`ضریب${prodUom && prodUom !== stockUom ? ` (${prodUom})` : ''}`}
                            value={lot.numerator}
                             onChange={(e) => {
                               const newList = [...lotsList];
                               newList[idx]!.numerator = e.target.value;
                               setLotsList(newList);
                            }}
                            dir="ltr"
                            style={{ flex: 1, minWidth: "10px", padding: "0.5rem", background: "rgba(255,255,255,0.05)", border: "1px solid var(--glass-border)", borderRadius: "6px", color: "var(--color-text)", fontSize: "0.85rem" }}
                          />
                          
                          <input
                            type="number"
                            step="0.01"
                            placeholder={`مخرج${stockUom ? ` (${stockUom})` : ''}`}
                            value={lot.denominator}
                             onChange={(e) => {
                               const newList = [...lotsList];
                               newList[idx]!.denominator = e.target.value;
                               setLotsList(newList);
                            }}
                            dir="ltr"
                            style={{ flex: 1, minWidth: "10px", padding: "0.5rem", background: "rgba(255,255,255,0.05)", border: "1px solid var(--glass-border)", borderRadius: "6px", color: "var(--color-text)", fontSize: "0.85rem" }}
                          />
                          
                          <button
                            className="btn btn-secondary"
                            style={{ padding: "0.3rem", color: "red", border: "none", background: "transparent" }}
                            onClick={() => {
                              if (lotsList.length > 1) {
                                setLotsList(lotsList.filter((_, i) => i !== idx));
                              }
                            }}
                            title="حذف"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      
                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.5rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                         <span>جمع مقادیر پالت‌ها:</span>
                         <span style={{ fontWeight: "bold", color: Math.abs(lotsList.reduce((acc, lot) => acc + Number(lot.quantity || 0), 0) - Number(quantity || 0)) < 0.0001 ? "var(--color-primary)" : "red" }}>
                           {lotsList.reduce((acc, lot) => acc + Number(lot.quantity || 0), 0)} / {quantity || 0}
                         </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* انتخاب پالت — خروج و انتقال */}
              {(txType === "issue" || txType === "transfer_out") && (
                <div style={{ flex: 2, minWidth: "180px" }}>
                  <label style={{ display: "block", marginBottom: "0.4rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                    پالت (اسکن یا انتخاب)
                  </label>
                  <ScannableInput
                    onScanned={handleLotScan}
                    disabled={isScanning || isPending}
                    placeholder="کد پالت را اسکن کنید..."
                  />
                  <div style={{ marginTop: '0.5rem' }}>
                    <select
                      value={selectedLotId}
                      onChange={async (e) => {
                        const lotId = e.target.value;
                        setSelectedLotId(lotId);
                        const chosen = lots.find((l: any) => l.id === lotId) || (scannedLot?.id === lotId ? scannedLot : null);
                        if (chosen && (chosen as any).lot_number) {
                          await handleLotScan((chosen as any).lot_number);
                        }
                      }}
                      disabled={!materialId}
                      style={{ width: "100%", padding: "0.6rem 0.9rem", background: "rgba(255,255,255,0.05)",
                        border: "1px solid var(--glass-border)", borderRadius: "8px", color: "var(--color-text)", boxSizing: "border-box" }}
                    >
                      <option value="">بدون انتخاب پالت</option>
                      {scannedLot && !lots.some((l: any) => l.id === scannedLot.id) && (
                        <option key={scannedLot.id} value={scannedLot.id}>
                          {scannedLot.lot_number} (اسکن‌شده)
                        </option>
                      )}
                      {lots.map((lot: any) => (
                        <option key={lot.id} value={lot.id}>{lot.lot_number}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
              {!createdLotId ? (
                <>
                  <button
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={isPending}
                  >
                    {isPending ? "در حال ثبت..." : "ثبت تراکنش"}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => { setShowForm(false); setError(null); }}
                  >
                    انصراف
                  </button>
                </>
              ) : (
                <>
                  <span style={{ color: 'var(--color-success)', marginRight: '1rem' }}>تراکنش با موفقیت ثبت شد.</span>
                  <button
                    className="btn btn-secondary"
                    onClick={() => navigate(`/inventory/lots/${createdLotId}/print?kind=compact`)}
                  >
                    چاپ برچسب کوچک
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => navigate(`/inventory/lots/${createdLotId}/print?kind=full`)}
                  >
                    چاپ برچسب کامل
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => { setShowForm(false); setCreatedLotId(""); }}
                  >
                    بستن
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* محتوای تب‌ها */}
        {activeTab === "on-hand" && (
          <div>
            <h2 className="font-bold mb-4">موجودی لحظه‌ای</h2>
            {onHandLoading ? (
              <p style={{ color: "var(--color-text-muted)" }}>در حال بارگذاری...</p>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "right" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--glass-border)" }}>
                    <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "normal" }}>انبار</th>
                    <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "normal" }}>متریال</th>
                    <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "normal" }}>موجودی</th>
                  </tr>
                </thead>
                <tbody>
                  {onHandStock.length === 0 ? (
                    <tr>
                      <td colSpan={3} style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                        هیچ موجودی ثبت نشده.
                      </td>
                    </tr>
                  ) : (
                    onHandStock.map((item, idx) => (
                      <tr key={idx} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                        <td style={{ padding: "0.85rem 1rem", fontSize: "0.9rem" }} dir="ltr">
                          {warehouses.find(w => w.id === String(item.warehouse_id))?.name || String(item.warehouse_id)}
                        </td>
                        <td style={{ padding: "0.85rem 1rem", fontSize: "0.9rem" }} dir="ltr">
                          {materials.find(m => m.id === String(item.material_id))?.code || String(item.material_id)}
                        </td>
                        <td style={{ padding: "0.85rem 1rem", fontFamily: "monospace" }} dir="ltr">
                          {item.on_hand}
                          {(() => {
                            const mat = materials.find(m => m.id === String(item.material_id));
                            const rev = mat?.latest_published_revision as any;
                            const uom = rev?.stock_uom_code || (mat as any)?.uom_code;
                            return uom ? ` ${uom}` : "";
                          })()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === "transactions" && (
          <div>
            <h2 className="font-bold mb-4">لیست تراکنش‌ها</h2>
            {txLoading ? (
              <p style={{ color: "var(--color-text-muted)" }}>در حال بارگذاری...</p>
            ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "right" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--glass-border)" }}>
                  <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "normal" }}>نوع</th>
                  <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "normal" }}>متریال</th>
                  <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "normal" }}>انبار</th>
                  <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "normal" }}>مقدار</th>
                  <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "normal" }}>سند مرجع</th>
                  <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "normal" }}>شناسه پالت</th>
                  <th style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)", fontWeight: "normal", textAlign: "center" }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {groupedTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                      هیچ تراکنشی ثبت نشده.
                    </td>
                  </tr>
                ) : (
                  groupedTransactions.map((item) => {
                    const materialObj = materials.find(m => m.id === String(item.material));
                    const uom = (materialObj?.latest_published_revision as any)?.stock_uom_code || (materialObj as any)?.uom_code;
                    const uomSuffix = uom ? ` ${uom}` : "";

                    if (item.isGroup) {
                      const isExpanded = expandedGroups[item.id];
                      return (
                        <React.Fragment key={item.id}>
                          <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", background: "rgba(255,255,255,0.02)" }}>
                            <td style={{ padding: "0.85rem 1rem" }}>
                              <span style={{
                                padding: "0.2rem 0.6rem", borderRadius: "100px",
                                background: item.transaction_type === "receipt" || item.transaction_type === "transfer_in"
                                  ? "rgba(0,255,0,0.1)" : "rgba(255,100,0,0.1)",
                                fontSize: "0.85rem",
                              }}>
                                {TX_TYPE_LABEL[item.transaction_type] ?? item.transaction_type}
                              </span>
                            </td>
                            <td style={{ padding: "0.85rem 1rem", fontSize: "0.9rem" }} dir="ltr">
                              {materialObj?.code || String(item.material)}
                            </td>
                            <td style={{ padding: "0.85rem 1rem", fontSize: "0.9rem" }} dir="ltr">
                              {warehouses.find(w => w.id === String(item.warehouse))?.name || String(item.warehouse)}
                            </td>
                            <td style={{ padding: "0.85rem 1rem", fontFamily: "monospace", fontWeight: "bold" }} dir="ltr">
                              {item.quantity}{uomSuffix}
                            </td>
                            <td style={{ padding: "0.85rem 1rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }} dir="ltr">
                              {item.reference_doc || "—"}
                            </td>
                            <td style={{ padding: "0.85rem 1rem", fontSize: "0.85rem", color: "var(--color-primary)", cursor: "pointer" }} onClick={() => toggleGroup(item.id)}>
                              {item.lots.length} پالت {isExpanded ? "▲" : "▼"}
                            </td>
                            <td style={{ padding: "0.85rem 1rem", fontSize: "0.85rem", textAlign: "center" }} dir="ltr">
                              <button className="btn btn-secondary" style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem", marginLeft: "0.5rem" }} onClick={() => navigate(`/inventory/print-batch?ref=${encodeURIComponent(item.reference_doc)}&kind=compact`)}>
                                گروهی کوچک
                              </button>
                              <button className="btn btn-secondary" style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem", marginLeft: "0.5rem" }} onClick={() => navigate(`/inventory/print-batch?ref=${encodeURIComponent(item.reference_doc)}&kind=full`)}>
                                گروهی کامل
                              </button>
                            </td>
                          </tr>
                          {isExpanded && item.lots.map((lotTx: any) => (
                            <tr key={lotTx.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.02)", background: "rgba(0,0,0,0.15)" }}>
                              <td style={{ padding: "0.6rem 1rem" }}></td>
                              <td style={{ padding: "0.6rem 1rem", fontSize: "0.8rem", color: "var(--color-text-muted)" }} dir="ltr">↳ پالت</td>
                              <td style={{ padding: "0.6rem 1rem" }}></td>
                              <td style={{ padding: "0.6rem 1rem", fontFamily: "monospace", fontSize: "0.85rem" }} dir="ltr">
                                {lotTx.quantity}{uomSuffix}
                              </td>
                              <td style={{ padding: "0.6rem 1rem" }}></td>
                              <td style={{ padding: "0.6rem 1rem", fontSize: "0.85rem" }} dir="ltr">
                                {lotTx.lot_number || "—"}
                              </td>
                              <td style={{ padding: "0.6rem 1rem", fontSize: "0.85rem", textAlign: "center" }} dir="ltr">
                                {((lotTx as any).lot || (lotTx as any).lot_id) && (
                                  <>
                                    <button className="btn btn-secondary" style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem", marginLeft: "0.5rem" }} onClick={() => navigate(`/inventory/lots/${(lotTx as any).lot_id || (lotTx as any).lot}/print?kind=compact`)}>
                                      کوچک
                                    </button>
                                    <button className="btn btn-secondary" style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem" }} onClick={() => navigate(`/inventory/lots/${(lotTx as any).lot_id || (lotTx as any).lot}/print?kind=full`)}>
                                      کامل
                                    </button>
                                  </>
                                )}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    }

                    // Single rendering
                    return (
                      <tr key={item.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                        <td style={{ padding: "0.85rem 1rem" }}>
                          <span style={{
                            padding: "0.2rem 0.6rem", borderRadius: "100px",
                            background: item.transaction_type === "receipt" || item.transaction_type === "transfer_in"
                              ? "rgba(0,255,0,0.1)" : "rgba(255,100,0,0.1)",
                            fontSize: "0.85rem",
                          }}>
                            {TX_TYPE_LABEL[item.transaction_type] ?? item.transaction_type}
                          </span>
                        </td>
                        <td style={{ padding: "0.85rem 1rem", fontSize: "0.9rem" }} dir="ltr">
                          {materialObj?.code || String(item.material)}
                        </td>
                        <td style={{ padding: "0.85rem 1rem", fontSize: "0.9rem" }} dir="ltr">
                          {warehouses.find(w => w.id === String(item.warehouse))?.name || String(item.warehouse)}
                        </td>
                        <td style={{ padding: "0.85rem 1rem", fontFamily: "monospace" }} dir="ltr">
                          {item.quantity}{uomSuffix}
                        </td>
                        <td style={{ padding: "0.85rem 1rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }} dir="ltr">
                          {item.reference_doc || "—"}
                        </td>
                        <td style={{ padding: "0.85rem 1rem", fontSize: "0.85rem" }} dir="ltr">
                          {item.lot_number || "—"}
                        </td>
                        <td style={{ padding: "0.85rem 1rem", fontSize: "0.85rem", textAlign: "center" }} dir="ltr">
                          {(item.lot || item.lot_id) && (
                            <>
                              <button className="btn btn-secondary" style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem", marginLeft: "0.5rem" }} onClick={() => navigate(`/inventory/lots/${item.lot_id || item.lot}/print?kind=compact`)}>
                                تکی کوچک
                              </button>
                              <button className="btn btn-secondary" style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem" }} onClick={() => navigate(`/inventory/lots/${item.lot_id || item.lot}/print?kind=full`)}>
                                تکی کامل
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
        )}
          </div>
        )}
      </div>
    </div>
  );
}